<template>
  <div
    ref="dropdownRef"
    :class="[
      'dropdown-container',
      { 'dropdown-container--webcomponent': isWebComponent },
    ]"
  >
    <div
      :class="[
        'dropdown',
        `dropdown--${size}`,
        `dropdown--${dropdownType}`,
        `dropdown--${dropdownMode}`,
        { 'dropdown--disabled': disabled, 'dropdown--readonly': readonly },
        width && `dropdown--width-${width}`,
        $attrs.class,
      ]"
      @click="toggleDropdown"
      @focusin="isFocused = true"
      @focusout="isFocused = false"
    >
    <input
      ref="inputRef"
      type="text"
      :id
      :value="displayValue"
      :placeholder="dynamicPlaceholder"
      :disabled
      :readonly
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @keydown="onKeyDown"
      @input="onInput"
    />

    <BasicAlert
      v-if="dropdownType === 'table' && alertLabel"
      :class="[`dropdown--${dropdownType}--alert`]"
      @click="showAlert(alertLabel)"
      :alertLabel
      :alertType
      :alertMaxlength="50"
      wrap
      :has-label="false"
      :hasBg="false"
    />

    <span class="dropdown__icons">
      <span
        v-if="couldClear"
        class="dropdown__icon-wrapper"
        :style="{ visibility: canClear ? 'visible' : 'hidden' }"
      >
        <BasicIcon
          name="close"
          pointer
          @click.stop="onClear"
        />
      </span>
      <BasicTooltip
        v-if="showLockIcon"
        :label="props.readOnlyReason ?? 'Lecture seule'"
        position="top"
      >
        <BasicIcon
          name="lock"
          class="dropdown__lock-icon dropdown__lock-icon--interactive"
        />
      </BasicTooltip>
      <span v-if="!showLockIcon" class="dropdown__icon-wrapper">
        <BasicIcon
          name="arrow-down"
          pointer
        />
      </span>
    </span>
    </div>
    <Teleport to="body" :disabled="isWebComponent">
      <ClickOutside
        v-if="isOpen && !disabled && hasFilteredItems"
        @close="closeFromOutside"
        class="dropdown__menu dropdown__menu--teleported"
        :style="isWebComponent ? undefined : dropdownMenuStyle"
        role="listbox"
      >
        <slot name="dropdown-items" />
      </ClickOutside>
    </Teleport>
  </div>
</template>

<script setup lang="ts" generic="TDropdownItem = DropdownItem">
  import { ref, computed } from 'vue'
  import { useAutoId, type DropdownItem, type DropdownMode, type DropdownProps } from '@designSystem/components'
  import { useDialog } from '@/features/interface/dialog'
  import { useDropdownMenuHandler } from './useDropdownMenuHandler'
  import type { DropdownContainerEvent } from './DropdownContainer.types'
  import { useParentClickOutsidePause } from '@designSystem/composables'
  import { useWebComponentNode } from '@/features/interface/composables/useWebComponentNode'

  defineOptions({ inheritAttrs: false })

  const id = useAutoId('input-dropdown')
  const dropdownRef = ref<HTMLElement | null>(null)
  const inputRef = ref<HTMLInputElement | null>(null)

  const webComponentNode = useWebComponentNode()
  const isWebComponent = computed(() => !!webComponentNode.shadowRoot)

  const emit = defineEmits<DropdownContainerEvent>()

  const isFocused = ref(false)

  const props = withDefaults(
    defineProps<DropdownProps<TDropdownItem> & { dropdownMode?: DropdownMode; filteredItemsCount?: number }>(),
    {
      size: 'medium',
      disabled: false,
      readonly: false,
      dropdownType: 'form',
      selectedLabel: '',
    },
  )

  const isOpen = defineModel<boolean>('open', { default: false })

  const hasFilteredItems = computed(() => props.filteredItemsCount === undefined || props.filteredItemsCount > 0)

  const showLockIcon = computed(() => {
    return props.readonly
  })

  const { dropdownMenuStyle, computedItems, updateDropdownVisibilityAndDirection } = useDropdownMenuHandler(
    props.items,
    props.keyId,
    props.keyLabel,
    props.keyIconName,
    dropdownRef,
    isOpen,
  )

  useParentClickOutsidePause(isOpen)

  const dynamicPlaceholder = computed(() =>
    !hasFilteredItems.value
      ? 'Aucun élément disponible'
      : props.readonly
        ? 'Sélection impossible (Lecture seule)'
        : props.placeholder,
  )

  const searchValue = ref('')
  const isSearching = computed(() => searchValue.value.length > 0)

  const displayValue = computed(() => (isSearching.value ? searchValue.value : props.selectedLabel))

  const couldClear = computed(() => props.deletable !== false || props.dropdownMode !== 'single')

  const canClear = computed(
    () =>
      !props.readonly &&
      !props.disabled &&
      (isFocused.value || isOpen.value) &&
      ((props.deletable !== false && !!props.selectedLabel && !props.forceValue && computedItems.value.length > 0) ||
        isSearching.value),
  )

  const showAlert = (message: string) => {
    const dialog = useDialog()
    dialog.showDialog({ message, type: 'Error', closable: true })
  }

  const closeFromOutside = (e: MouseEvent) => {
    if (!isOpen.value) return
    if (dropdownRef.value?.contains(e.target as Node)) return
    isOpen.value = false
  }

  const toggleDropdown = async () => {
    if (props.disabled || props.readonly) return
    isOpen.value = !isOpen.value
    if (isOpen.value) updateDropdownVisibilityAndDirection()
  }

  const onKeyDown = (event: KeyboardEvent) => {
    const allowedKeys = ['Tab', 'Enter', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ']
    if (props.dropdownMode === 'single' && !allowedKeys.includes(event.key)) {
      event.preventDefault()
    }
    emit('onKeyDown', event)
  }

  const onInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    searchValue.value = target.value
    emit('search', target.value)
    if (!isOpen.value && target.value.length > 0) {
      isOpen.value = true
      updateDropdownVisibilityAndDirection()
    }
  }

  const close = () => {
    isOpen.value = false
    searchValue.value = ''
  }

  const open = () => {
    if (props.disabled || props.readonly) return
    isOpen.value = true
    updateDropdownVisibilityAndDirection()
  }

  const focus = () => {
    inputRef.value?.focus()
  }

  const onClear = () => {
    if (isSearching.value) {
      searchValue.value = ''
      emit('search', '')
    } else {
      emit('deleting', null)
    }
  }

  defineExpose({ close, open, focus, isOpen })
</script>

<style lang="less">
  @import 'DropdownContainer.less';
</style>
